import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getCtrlAllComplaints } from '../../../../redux/actions/SocietyController/CtrlDashboardAction/CtrlDashboardAction';

/**
 * Cross-society complaints list for the webmaster dashboard.
 * Backed by GET /api/complaints/all (super admin only).
 *
 * Props:
 *   societyId — '' for all societies, or the society picked in the
 *               Operations Overview filter (shared so both sections agree)
 */

const STATUS_OPTIONS = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'REOPENED'];
const PRIORITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const LIMIT_OPTIONS = [10, 20, 50, 100];

const STATUS_STYLE = {
    PENDING:     'tw-bg-amber-100 tw-text-amber-700',
    IN_PROGRESS: 'tw-bg-blue-100 tw-text-blue-700',
    COMPLETED:   'tw-bg-emerald-100 tw-text-emerald-700',
    REOPENED:    'tw-bg-rose-100 tw-text-rose-700'
};

const PRIORITY_STYLE = {
    LOW:    'tw-bg-gray-100 tw-text-gray-600',
    MEDIUM: 'tw-bg-blue-50 tw-text-blue-600',
    HIGH:   'tw-bg-amber-50 tw-text-amber-700',
    URGENT: 'tw-bg-rose-50 tw-text-rose-600'
};

const humanize = (s) => (s || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

const fullName = (u) => {
    if (!u) return '';
    const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return full || u.email || `User #${u.user_id}`;
};

const fmtDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// due_date is a plain YYYY-MM-DD — build a local date so it doesn't shift a
// day in timezones behind UTC.
const parseDueDate = (value) => {
    const [y, m, d] = String(value || '').split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

const isOverdue = (row) => {
    const due = parseDueDate(row.due_date);
    if (!due || row.status === 'COMPLETED') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
};

const Pill = ({ value, styles }) => (
    <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold tw-whitespace-nowrap ${styles[value] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
        {humanize(value) || '—'}
    </span>
);

const selectCls = 'tw-px-2 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md tw-bg-white focus:tw-outline-none focus:tw-border-rose-500';
const thCls = 'tw-text-left tw-px-4 tw-py-2.5 tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-text-[10px] tw-whitespace-nowrap';

const AllComplaintsSection = ({ societyId = '' }) => {
    const dispatch = useDispatch();
    const {
        allComplaints: rows = [],
        allComplaintsPagination: pagination,
        allComplaintsLoading: loading,
        allComplaintsError: error
    } = useSelector((s) => s.CtrlDashboardReducer || {});

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [status, setStatus] = useState('');
    const [priority, setPriority] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    // Switching society invalidates the current page number. Resetting during
    // render (rather than in an effect) avoids one request with the stale page.
    const [prevSocietyId, setPrevSocietyId] = useState(societyId);
    if (prevSocietyId !== societyId) {
        setPrevSocietyId(societyId);
        setPage(1);
    }

    // Debounce typing so each keystroke isn't a request
    useEffect(() => {
        const t = setTimeout(() => {
            const next = searchInput.trim();
            if (next !== search) {
                setSearch(next);
                setPage(1);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput, search]);

    const fetchList = useCallback(() => {
        dispatch(getCtrlAllComplaints({
            society_id: societyId ? Number(societyId) : undefined,
            page,
            limit,
            status,
            priority,
            search
        }));
    }, [dispatch, societyId, page, limit, status, priority, search]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const total = Number(pagination?.total || 0);
    const totalPages = Number(pagination?.totalPages || 1);
    const firstRecord = total === 0 ? 0 : (page - 1) * limit + 1;
    const lastRecord = Math.min(page * limit, total);
    const hasFilters = Boolean(status || priority || search);

    const clearFilters = () => {
        setStatus('');
        setPriority('');
        setSearchInput('');
        setSearch('');
        setPage(1);
    };

    return (
        <div className="col-12">
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:message-alert-outline" className="tw-text-xl tw-text-rose-600" />
                        <h4 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mb-0">All Complaints</h4>
                        <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold tw-bg-gray-100 tw-text-gray-600">{total}</span>
                        {loading && (
                            <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                        )}
                    </div>

                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        <div className="tw-relative">
                            <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400" />
                            <input
                                type="text"
                                value={searchInput}
                                maxLength={200}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search subject or code..."
                                className="tw-w-[200px] tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 tw-bg-white"
                            />
                        </div>
                        <select
                            value={status}
                            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                            className={selectCls}
                        >
                            <option value="">All statuses</option>
                            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{humanize(s)}</option>)}
                        </select>
                        <select
                            value={priority}
                            onChange={(e) => { setPriority(e.target.value); setPage(1); }}
                            className={selectCls}
                        >
                            <option value="">All priorities</option>
                            {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{humanize(p)}</option>)}
                        </select>
                        {hasFilters && (
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="tw-text-[11px] tw-font-medium tw-text-gray-500 hover:tw-text-gray-800 tw-underline"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {error && !loading && (
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-m-3 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-rose-50 tw-border tw-border-rose-100 tw-text-xs tw-text-rose-700">
                        <span className="tw-flex tw-items-center tw-gap-1.5">
                            <Icon icon="mdi:alert-circle-outline" className="tw-text-base" />
                            Couldn't load complaints: {error}
                        </span>
                        <button type="button" onClick={fetchList} className="tw-font-medium tw-underline hover:tw-text-rose-800">
                            Retry
                        </button>
                    </div>
                )}

                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                                <th className={thCls}>Complaint</th>
                                <th className={thCls}>Society</th>
                                <th className={thCls}>Category</th>
                                <th className={thCls}>Flat</th>
                                <th className={thCls}>Raised By</th>
                                <th className={thCls}>Assigned To</th>
                                <th className={thCls}>Priority</th>
                                <th className={thCls}>Status</th>
                                <th className={thCls}>Due</th>
                                <th className={thCls}>Created</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-gray-50">
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="tw-text-center tw-text-gray-400 tw-py-8">
                                        {loading
                                            ? 'Loading complaints…'
                                            : hasFilters
                                                ? 'No complaints match these filters'
                                                : 'No complaints found'}
                                    </td>
                                </tr>
                            ) : rows.map((r) => {
                                const overdue = isOverdue(r);
                                const due = parseDueDate(r.due_date);
                                return (
                                    <tr key={r.complaint_id} className="hover:tw-bg-slate-50/50 tw-transition-colors">
                                        <td className="tw-px-4 tw-py-2.5 tw-max-w-[260px]">
                                            <div className="tw-font-medium tw-text-gray-800 tw-truncate" title={r.subject}>{r.subject || '—'}</div>
                                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-[10px] tw-text-gray-400">
                                                <span className="tw-font-mono">{r.complaint_code}</span>
                                                {r.attachments?.length > 0 && (
                                                    <span className="tw-inline-flex tw-items-center tw-gap-0.5" title={`${r.attachments.length} attachment(s)`}>
                                                        <Icon icon="mdi:paperclip" /> {r.attachments.length}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5">
                                            <div className="tw-text-gray-700">{r.society?.name || `Society #${r.society_id}`}</div>
                                            {r.society?.society_code && (
                                                <div className="tw-text-[10px] tw-text-gray-400 tw-font-mono">{r.society.society_code}</div>
                                            )}
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{r.category?.name || '—'}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700 tw-whitespace-nowrap">
                                            {r.flat?.flat_number || '—'}
                                            {r.wing?.title && <span className="tw-text-gray-400"> · {r.wing.title}</span>}
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{fullName(r.raisedBy) || '—'}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">
                                            {r.assignedTo ? fullName(r.assignedTo) : <span className="tw-text-gray-400">Unassigned</span>}
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5"><Pill value={r.priority} styles={PRIORITY_STYLE} /></td>
                                        <td className="tw-px-4 tw-py-2.5"><Pill value={r.status} styles={STATUS_STYLE} /></td>
                                        <td className={`tw-px-4 tw-py-2.5 tw-whitespace-nowrap ${overdue ? 'tw-text-rose-600 tw-font-semibold' : 'tw-text-gray-500'}`}>
                                            {due ? fmtDateTime(due) : '—'}
                                            {overdue && <Icon icon="mdi:timer-alert-outline" className="tw-inline tw-ml-1" title="Overdue" />}
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-500 tw-whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-border-t tw-border-gray-100 tw-text-xs tw-text-gray-500">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <span>Rows</span>
                        <select
                            value={limit}
                            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                            className={selectCls}
                        >
                            {LIMIT_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <span>Showing {firstRecord} to {lastRecord} of {total}</span>
                        <div className="tw-flex tw-items-center tw-gap-1">
                            <button
                                type="button"
                                disabled={page <= 1 || loading}
                                onClick={() => setPage(p => p - 1)}
                                className="tw-p-1.5 tw-rounded tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                            >
                                <Icon icon="mdi:chevron-left" className="tw-text-base" />
                            </button>
                            <span className="tw-px-2 tw-font-medium tw-text-gray-700">{page} / {totalPages}</span>
                            <button
                                type="button"
                                disabled={page >= totalPages || loading}
                                onClick={() => setPage(p => p + 1)}
                                className="tw-p-1.5 tw-rounded tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                            >
                                <Icon icon="mdi:chevron-right" className="tw-text-base" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AllComplaintsSection;
